import React, { ReactNode, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ListCard } from '../../../../components/common/ListCard'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { useAuth } from '../../../../hooks/useAuth'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { unitQueries } from '../../../../api/units'
import { theme } from '../../../../theme/theme'
import { Spinner } from '../../../../components/common/Spinner'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { unitString } from '../../../../hooks/useUnitsString'
import { entranceQueries } from '../../../../api/society-entrances'
import { binderQueries } from '../../../../api/binder'

export const UnitPickerView = (): ReactNode => {
  const { userId } = useAuth()
  const navigate = useNavigate()
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const { isLoading, data: units } = useQuery(unitQueries.getUserUnits(userId))
  const { data: userBinders } = useQuery(
    binderQueries.userHasBinders(society?._id || '')
  )
  const societyUnits = units?.filter((unit) => unit.societyId === society?._id)
  const { data: societyEntrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )

  const unitsWithBinders = societyUnits?.filter((unit) =>
    userBinders?.some((binder) => binder.unitId === unit._id)
  )

  useDocumentTitle(reverseDocumentTitle('society:binder'))

  useEffect(() => {
    if (!isLoading && unitsWithBinders?.length === 1) {
      navigate(
        reverseUrl('society:binder-unit', {
          id: society?._id as string,
          unitId: unitsWithBinders[0]._id,
        }),
        { replace: true }
      )
    }
  }, [isLoading, navigate, society?._id, unitsWithBinders, units])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (unitsWithBinders?.length === 1) {
    return null
  }

  return (
    <ErrorBoundary>
      <ViewBase title={translate('unitPickerView.title')} loading={isLoading}>
        <div className="space-y-4">
          {unitsWithBinders?.map((unit) => (
            <ListCard
              key={unit._id}
              toPath={reverseUrl('society:binder-unit', {
                id: society?._id as string,
                unitId: unit._id,
              })}
            >
              <div className="flex items-center">
                <p
                  style={theme.textVariants.baseBold}
                  className="ml-4 line-clamp-1"
                >
                  {unitString(
                    [unit],
                    society ? [society] : [],
                    societyEntrances || [],
                    true
                  )}
                </p>
              </div>
            </ListCard>
          ))}
        </div>
      </ViewBase>
    </ErrorBoundary>
  )
}
